import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Filler,
    Legend,
    BarElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import PageLayout from '../../components/layout/PageLayout';
import DataTable from '../../components/ui/DataTable';
import ChartCard from '../../components/ui/ChartCard';
import { baseChartOptions, barDataset, lineDataset } from '../../components/ui/chartConfig';
import { notify } from '../../components/feedback/notify';
import { MascaraTelefone } from "../../utils/MascaraTelefone";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Filler,
    Legend,
    BarElement,
);


const dddLocalizacoes = [
    { ddd: 11, loc: 'São Paulo' },
    { ddd: 12, loc: 'Vale do Paraíba' },
    { ddd: 13, loc: 'Santos' },
    { ddd: 14, loc: 'Bauru' },
    { ddd: 15, loc: 'Sorocaba' },
    { ddd: 16, loc: 'Ribeirão Preto' },
    { ddd: 17, loc: 'São José do Rio Preto' },
    { ddd: 18, loc: 'Presidente Prudente' },
    { ddd: 19, loc: 'Campinas' },
    { ddd: 21, loc: 'Rio de Janeiro' },
    { ddd: 22, loc: 'Campos dos Goytacazes' },
    { ddd: 24, loc: 'Volta Redonda' },
    { ddd: 27, loc: 'Vitória' },
    { ddd: 28, loc: 'Grande Vitória' },
    { ddd: 31, loc: 'Belo Horizonte' },
    { ddd: 32, loc: 'Juiz de Fora' },
    { ddd: 33, loc: 'Governador Valadares' },
    { ddd: 34, loc: 'Uberaba' },
    { ddd: 35, loc: 'Poços de Caldas' },
    { ddd: 37, loc: 'Divinópolis' },
    { ddd: 38, loc: 'Montes Claros' },
    { ddd: 41, loc: 'Curitiba' },
    { ddd: 42, loc: 'Ponta Grossa' },
    { ddd: 43, loc: 'Londrina' },
    { ddd: 44, loc: 'Maringá' },
    { ddd: 45, loc: 'Foz do Iguaçu' },
    { ddd: 46, loc: 'Cascavel' },
    { ddd: 47, loc: 'Joinville' },
    { ddd: 48, loc: 'Florianópolis' },
    { ddd: 49, loc: 'Chapecó' },
    { ddd: 51, loc: 'Porto Alegre' },
    { ddd: 53, loc: 'Pelotas' },
    { ddd: 54, loc: 'Caxias do Sul' },
    { ddd: 55, loc: 'Santa Maria' },
    { ddd: 61, loc: 'Brasília' },
    { ddd: 62, loc: 'Goiânia' },
    { ddd: 63, loc: 'Palmas' },
    { ddd: 64, loc: 'Rio Verde' },
    { ddd: 65, loc: 'Cuiabá' },
    { ddd: 66, loc: 'Rondonópolis' },
    { ddd: 67, loc: 'Campo Grande' },
    { ddd: 68, loc: 'Rio Branco' },
    { ddd: 69, loc: 'Porto Velho' },
    { ddd: 71, loc: 'Salvador' },
    { ddd: 73, loc: 'Ilhéus' },
    { ddd: 74, loc: 'Juazeiro' },
    { ddd: 75, loc: 'Feira de Santana' },
    { ddd: 77, loc: 'Barreiras' },
    { ddd: 79, loc: 'Aracaju' },
    { ddd: 81, loc: 'Recife' },
    { ddd: 82, loc: 'Maceió' },
    { ddd: 83, loc: 'João Pessoa' },
    { ddd: 84, loc: 'Natal' },
    { ddd: 85, loc: 'Fortaleza' },
    { ddd: 86, loc: 'Teresina' },
    { ddd: 87, loc: 'Caruaru' },
    { ddd: 88, loc: 'Sobral' },
    { ddd: 89, loc: 'Picos' },
    { ddd: 91, loc: 'Belém' },
    { ddd: 92, loc: 'Manaus' },
    { ddd: 93, loc: 'Santarém' },
    { ddd: 94, loc: 'Marabá' },
    { ddd: 95, loc: 'Boa Vista' },
    { ddd: 96, loc: 'Macapá' },
    { ddd: 97, loc: 'Interior do Amazonas' },
    { ddd: 98, loc: 'São Luís' },
    { ddd: 99, loc: 'Imperatriz' }
];

// Ordena as categorias por quantidade (maior primeiro) para leitura mais rápida.
const ordenarPorQuantidade = (contagem) =>
    Object.entries(contagem).sort((a, b) => b[1] - a[1]);

const formatData = (data) => {
    if (!data) return '—';
    const options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };
    return new Date(data).toLocaleString('pt-BR', options);
};

function LeadsLandingPage() {

    const token = localStorage.getItem('token');
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ labels: [], datasets: [barDataset('Leads por área de trabalho', [])] });
    const [dataLoc, setDataLoc] = useState({ labels: [], datasets: [barDataset('Leads por localização', [])] });
    const [dataHorario, setDataHorario] = useState({ labels: [], datasets: [lineDataset('Leads por horário', [])] });

    useEffect(() => {
        const getUsuarios = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/listarLeads`, {
                    headers: {
                        Authorization: token
                    }
                });

                if (response.data.status === 200) {
                    const leads = response.data.leads || [];
                    setUsuarios(leads);

                    // Leads por horário (0h a 23h, incluindo horários sem envio)
                    const contagemHorarios = Array.from({ length: 24 }, () => 0);
                    leads.forEach((lead) => {
                        const hora = new Date(lead.createdAt).getHours();
                        if (!Number.isNaN(hora)) contagemHorarios[hora] += 1;
                    });

                    setDataHorario({
                        labels: contagemHorarios.map((_, hora) => `${String(hora).padStart(2, '0')}h`),
                        datasets: [lineDataset('Leads por horário', contagemHorarios)],
                    });

                    // Leads por área de trabalho
                    const contagemAreas = leads.reduce((acc, lead) => {
                        const area = lead.areaTrabalho || 'Não informado';
                        acc[area] = (acc[area] || 0) + 1;
                        return acc;
                    }, {});
                    const areasOrdenadas = ordenarPorQuantidade(contagemAreas);

                    setData({
                        labels: areasOrdenadas.map(([area]) => area),
                        datasets: [barDataset('Leads por área de trabalho', areasOrdenadas.map(([, qtd]) => qtd))],
                    });

                    // Leads por localização (a partir do DDD do telefone)
                    const contagemDDD = leads.reduce((acc, lead) => {
                        const ddd = Number(String(lead.telefone || '').replace(/\D/g, '').substring(0, 2));
                        const localizacao = dddLocalizacoes.find(item => item.ddd === ddd);

                        if (localizacao) {
                            acc[localizacao.loc] = (acc[localizacao.loc] || 0) + 1;
                        }

                        return acc;
                    }, {});
                    const locOrdenadas = ordenarPorQuantidade(contagemDDD);

                    setDataLoc({
                        labels: locOrdenadas.map(([loc]) => loc),
                        datasets: [barDataset('Leads por localização', locOrdenadas.map(([, qtd]) => qtd))],
                    });
                }
            } catch (error) {
                console.error("Erro ao listar leads:", error);
                notify.error("Não foi possível carregar os leads da landing page.");
            } finally {
                setLoading(false);
            }
        }

        getUsuarios();
    }, [token])

    const columns = [
        { key: 'idLeadForm', label: 'ID', width: 72 },
        { key: 'nome', label: 'Nome', sx: { fontWeight: 500 }, render: (l) => l?.nome || '—' },
        { key: 'email', label: 'E-mail', render: (l) => l?.email || '—' },
        { key: 'telefone', label: 'Telefone', render: (l) => MascaraTelefone(l?.telefone) || '—' },
        { key: 'areaTrabalho', label: 'Área de trabalho', render: (l) => l?.areaTrabalho || '—' },
        { key: 'formToken', label: 'Token', render: (l) => <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.75rem', color: 'text.secondary' }}>{l?.formToken || '—'}</Box> },
        { key: 'createdAt', label: 'Enviado em', render: (l) => formatData(l?.createdAt) },
    ];

    const chartHeight = (labels) => Math.max(220, Math.min(labels.length * 28, 420));

    return (
        <PageLayout
            title="Leads da landing page"
            subtitle="Formulários enviados pelo site e distribuição por origem, região e horário."
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Leads landing page' }]}
            actions={
                <Tooltip title="Em desenvolvimento">
                    <span>
                        <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled>
                            Baixar CSV
                        </Button>
                    </span>
                </Tooltip>
            }
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Card sx={{ px: 2.5, py: 2, gridColumn: { lg: '1 / -1' } }}>
                    <Typography variant="body2" color="text.secondary">Formulários enviados</Typography>
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, mt: 0.25 }}>
                        {loading ? '—' : usuarios.length.toLocaleString('pt-BR')}
                    </Typography>
                </Card>

                <ChartCard
                    title="Leads por área de trabalho"
                    loading={loading}
                    empty={data.labels.length === 0}
                    height={chartHeight(data.labels)}
                >
                    <Bar data={data} options={baseChartOptions({ horizontal: true })} />
                </ChartCard>

                <ChartCard
                    title="Leads por localização"
                    description="Estimada pelo DDD do telefone informado."
                    loading={loading}
                    empty={dataLoc.labels.length === 0}
                    height={chartHeight(dataLoc.labels)}
                >
                    <Bar data={dataLoc} options={baseChartOptions({ horizontal: true })} />
                </ChartCard>

                <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
                    <ChartCard
                        title="Leads por horário"
                        description="Horário de envio do formulário."
                        loading={loading}
                        empty={usuarios.length === 0}
                        height={240}
                    >
                        <Line data={dataHorario} options={baseChartOptions()} />
                    </ChartCard>
                </Box>
            </Box>

            <DataTable
                columns={columns}
                rows={usuarios}
                getRowKey={(l, i) => l?.idLeadForm ?? i}
                loading={loading}
                emptyMessage="Nenhum formulário enviado."
            />
        </PageLayout>
    );
}

export default LeadsLandingPage;
